/**
 * This month: the open month's time-and-materials projects, live.
 *
 * Margin so far is hours already worked, priced at the bill rate and at each
 * person's trailing cost rate. The projection adds each person's remaining
 * guidance and takes off pending discounts. Every figure is the engine's; this
 * page sorts, filters and renders, and computes nothing.
 *
 * The rule that matters most: a margin the engine could not price is null, and
 * this page says "No cost rate" -- never a percentage. A missing cost read as
 * zero is a 100% margin, the most flattering number a project can show and the
 * one most certainly wrong.
 *
 * Aggregate callers get their book, margin percent only; the hours and money
 * columns go rather than turning into dashes (docs/DECISIONS.md, 2026-10-07).
 */
import { Link } from "@tanstack/react-router";
import { CalendarClock } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { HBars } from "@/components/charts/HBars";
import { PageHeaderBar } from "@/components/layout/PageHeaderBar";
import { usePageHeader } from "@/components/layout/PageHeaderContext";
import { EmptyBook, isEmptyBook, YourBook } from "@/components/profitability/BookNotice";
import {
  ActiveFilterBar,
  FilterButton,
  FilterDialog,
} from "@/components/profitability/FilterDialog";
import {
  applyFilters,
  buildFilterOptions,
  emptyFilterValues,
  type FilterDef,
} from "@/components/profitability/filters";
import { MarginFigure } from "@/components/profitability/MarginFigure";
import {
  costCoverage,
  flagCount,
  MtdFlags,
  MtdMargin,
  type MtdRow,
  pricedLanding,
  pricedSoFar,
} from "@/components/profitability/MonthToDate";
import { DataTable, ROW_CLASS } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { type HeroFact, HeroPanel } from "@/components/shared/hero-panel";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useClientSort } from "@/hooks/use-client-sort";
import { useFilterParams } from "@/hooks/use-filter-params";
import { isAggregateMonthToDate, useMonthToDate } from "@/hooks/use-month-to-date";
import { COLUMN_HELP } from "@/lib/column-help";
import { calendarDay, hours as formatHours, money } from "@/lib/format";

/** A row of either tier. The hours and money keys are absent for Aggregate. */
type Row = MtdRow;

const METHOD_NOTE =
  "Cost: each person's average over the last three closed months. Projection adds remaining guidance.";

const EMPTY_ROWS: Row[] = [];

/**
 * Filters over the rows the page holds. Module-level for a stable identity.
 * Projects and accounts are named; status and cost coverage are the two flag
 * families a reader filters on ("show me what is not priced").
 */
const FILTERS: FilterDef<Row>[] = [
  {
    key: "account",
    label: "Account",
    valueOf: (row) => row.account_name,
    help: "Accounts with time-and-materials work this month.",
  },
  {
    key: "project",
    label: "Project",
    valueOf: (row) => row.demand_name,
    help: "Time-and-materials projects with hours or guidance this month.",
  },
  {
    key: "started",
    label: "Started",
    valueOf: (row) => (row.has_started ? "Started" : "Not started"),
    help: "Not started: guidance this month, no hours logged yet.",
  },
  {
    key: "cost",
    label: "Cost coverage",
    valueOf: (row) =>
      ({ complete: "Fully priced", partial: "Partial cost", none: "No cost rate" })[
        costCoverage(row)
      ],
    help: "Whether every hour has a cost rate behind it.",
  },
];
const FILTER_KEYS = FILTERS.map((def) => def.key);
const FILTER_SCOPE = "Filters the table only. The figures above it are the whole month's totals.";

export function ThisMonthPage() {
  const { data, isLoading, isError, error, refetch } = useMonthToDate();
  const aggregate = data && isAggregateMonthToDate(data) ? data : null;
  const full = data && !isAggregateMonthToDate(data) ? data : null;
  const rows: Row[] = (data?.projects as Row[] | undefined) ?? EMPTY_ROWS;

  const [filterValues, setFilterValues] = useFilterParams(FILTER_KEYS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtered = useMemo(() => applyFilters(rows, FILTERS, filterValues), [rows, filterValues]);
  const filterOptions = useMemo(() => buildFilterOptions(rows, FILTERS), [rows]);

  const accessor = useCallback((row: Row, key: string) => {
    switch (key) {
      case "demand_name":
        return row.demand_name;
      case "account_name":
        return row.account_name;
      case "hours":
        return row.hours;
      case "margin_pct":
        return row.margin_pct;
      case "landing_hours":
        return row.landing_hours;
      case "landing_revenue":
        return row.landing_revenue;
      case "landing_margin_pct":
        return row.landing_margin_pct;
      case "flags":
        return flagCount(row);
      default:
        return null;
    }
  }, []);
  const sorted = useClientSort<Row>(
    filtered,
    accessor,
    // The engine's own order for full detail; Aggregate has no revenue to
    // order on, so the projected margin, worst first.
    aggregate ? { key: "landing_margin_pct", dir: "asc" } : { key: "landing_revenue", dir: "desc" },
  );

  const period = data?.period;
  const asOf = data?.as_of ?? null;
  usePageHeader(
    () => (
      <PageHeaderBar
        title="This month"
        fields={[
          period
            ? {
                key: "month",
                label: "Month",
                content: <span className="text-sm font-medium">{period.name}</span>,
              }
            : null,
          period
            ? {
                key: "as-of",
                label: "Live hours",
                content: (
                  <span
                    className="text-sm font-medium"
                    title="The latest day with time counted. Never after today."
                  >
                    {asOf ? `Hours through ${calendarDay(asOf)}` : "No hours logged yet"}
                  </span>
                ),
              }
            : null,
        ]}
        filters={<FilterButton values={filterValues} onOpen={() => setFiltersOpen(true)} />}
        range={undefined}
      />
    ),
    [period, asOf, filterValues],
  );

  if (isError) {
    return (
      <div className="w-full space-y-4">
        <ErrorState message={(error as Error)?.message} onRetry={() => refetch()} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="w-full space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (aggregate && isEmptyBook(aggregate.access)) {
    return (
      <div className="w-full space-y-4">
        <EmptyBook access={aggregate.access} />
      </div>
    );
  }

  const totals = data.totals;
  const futureHours = full?.future_dated_hours ?? 0;
  // Time and plan on an assignment NOS no longer has: no demand, so on no row
  // and in no total. Counted by the engine, said here; full tier only, as the
  // aggregate payload carries no hours.
  const orphanHours = full?.orphan_hours ?? 0;
  const orphanGuidance = full?.orphan_guidance_hours ?? 0;
  const orphanParts = [
    orphanHours > 0 ? `${formatHours(orphanHours)} h logged` : null,
    orphanGuidance > 0 ? `${formatHours(orphanGuidance)} h planned` : null,
  ].filter(Boolean);
  const orphanNote =
    orphanParts.length > 0
      ? `${orphanParts.join(" and ")} this month are on no project assignment in NOS, so not in any figure below.`
      : null;
  const excluded = totals.excluded_unpriced;
  // The engine's totals sum fully priced projects only -- revenue as well as
  // margin -- so one note covers the whole row. When every project is left
  // out there is nothing to total: a dash, not $0 or a percent.
  const nothingPriced = rows.length > 0 && excluded === rows.length;
  const excludedNote =
    excluded > 0 && !nothingPriced
      ? `${excluded} project${excluded === 1 ? "" : "s"} with hours that have no cost rate ${
          excluded === 1 ? "is" : "are"
        } left out of these totals.`
      : null;
  const noPriced = "No priced projects";
  const dash = "—";
  const facts: HeroFact[] = [
    ...(full
      ? [
          {
            label: "Revenue so far",
            value: nothingPriced ? dash : money(full.totals.revenue),
          },
        ]
      : []),
    {
      label: "Margin so far",
      value: nothingPriced ? dash : <MarginFigure value={totals.margin_pct} />,
    },
    ...(full
      ? [
          {
            label: "Projected revenue",
            value: nothingPriced ? dash : money(full.totals.landing_revenue),
          },
        ]
      : []),
    ...(excluded > 0
      ? [
          {
            label: "No cost rate",
            value: `${excluded} project${excluded === 1 ? "" : "s"}`,
            tone: "bad" as const,
          },
        ]
      : []),
  ];
  const idle = full?.discounts_without_activity_items ?? [];

  return (
    <div className="w-full space-y-4">
      <YourBook
        access={aggregate?.access}
        count={rows.length}
        noun={["project", "projects"]}
        scope="with time-and-materials work this month"
      />

      <div className="space-y-2">
        <div data-testid="kpi-row">
          <HeroPanel
            eyebrow={`Projected margin · ${data.period.name}`}
            figure={nothingPriced ? "—" : <MarginFigure value={totals.landing_margin_pct} />}
            note={
              nothingPriced
                ? noPriced
                : full && full.totals.discount > 0
                  ? `${METHOD_NOTE} Projected revenue is after ${money(full.totals.discount)} of pending discounts.`
                  : METHOD_NOTE
            }
            chart={
              full && !nothingPriced && full.totals.landing_revenue > 0 ? (
                <HBars
                  label="Revenue so far against projected revenue"
                  max={Math.max(full.totals.revenue, full.totals.landing_revenue)}
                  rows={[
                    {
                      label: "Revenue so far",
                      value: full.totals.revenue,
                      display: money(full.totals.revenue),
                    },
                    {
                      label: "Guidance left",
                      value: full.totals.landing_revenue - full.totals.revenue,
                      display: "",
                      series: 2,
                    },
                    {
                      label: "Projected",
                      value: full.totals.landing_revenue,
                      display: money(full.totals.landing_revenue),
                    },
                  ]}
                />
              ) : undefined
            }
            facts={facts}
          />
        </div>
        {excludedNote ? (
          <p data-testid="kpi-note" className="text-xs text-muted-foreground">
            {excludedNote}
          </p>
        ) : null}
        {futureHours > 0 || orphanNote ? (
          <div className="space-y-1 text-xs text-muted-foreground">
            {futureHours > 0 ? (
              <p>{formatHours(futureHours)} h logged for later this month are not counted yet.</p>
            ) : null}
            {orphanNote ? <p data-testid="orphan-note">{orphanNote}</p> : null}
          </div>
        ) : null}
      </div>

      <FilterDialog
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        defs={FILTERS}
        options={filterOptions}
        values={filterValues}
        onApply={setFilterValues}
        scopeNote={FILTER_SCOPE}
      />
      <ActiveFilterBar
        defs={FILTERS}
        values={filterValues}
        shown={filtered.length}
        total={rows.length}
        scopeNote={FILTER_SCOPE}
        onEdit={() => setFiltersOpen(true)}
        onClear={() => setFilterValues(emptyFilterValues(FILTERS))}
      />

      <Card flush className="w-full pt-0">
        <DataTable
          flush
          dense
          loading={isLoading}
          empty={
            <EmptyState
              icon={CalendarClock}
              title={
                rows.length === 0
                  ? `No time-and-materials work in ${data.period.name} yet`
                  : "No projects match these filters"
              }
              description={
                rows.length === 0
                  ? "Projects appear here once they have hours or guidance this month."
                  : "Clear a filter to see more."
              }
            />
          }
          sort={sorted.sort}
          sortDir={sorted.sortDir}
          onSort={sorted.toggleSort}
          headers={[
            { label: "Project", sortKey: "demand_name", help: COLUMN_HELP.project },
            { label: "Account", sortKey: "account_name", help: COLUMN_HELP.account },
            ...(full
              ? [
                  {
                    label: "Hours so far",
                    sortKey: "hours",
                    className: "text-right",
                    help: COLUMN_HELP.mtdHours,
                  },
                ]
              : []),
            {
              label: "Margin so far",
              sortKey: "margin_pct",
              className: "text-right",
              help: COLUMN_HELP.mtdMargin,
            },
            ...(full
              ? [
                  {
                    label: "Projected hours",
                    sortKey: "landing_hours",
                    className: "text-right",
                    help: COLUMN_HELP.mtdProjectedHours,
                  },
                  {
                    label: "Projected revenue",
                    sortKey: "landing_revenue",
                    className: "text-right",
                    help: COLUMN_HELP.mtdProjectedRevenue,
                  },
                ]
              : []),
            {
              label: "Projected margin",
              sortKey: "landing_margin_pct",
              className: "text-right",
              help: COLUMN_HELP.mtdProjectedMargin,
            },
            { label: "Flags", sortKey: "flags", help: COLUMN_HELP.mtdFlags },
          ]}
        >
          {sorted.rows.map((row) => (
            <tr key={row.demand_id} className={ROW_CLASS}>
              <td className="min-w-44 px-4 py-2.5 font-medium">
                <Link
                  to="/dashboard/projects/$demandId"
                  params={{ demandId: row.demand_id }}
                  className="hover:underline"
                >
                  {row.demand_name ?? row.demand_id}
                </Link>
              </td>
              <td className="max-w-44 truncate px-4 py-2.5" title={row.account_name ?? undefined}>
                {row.account_name ?? "—"}
              </td>
              {full ? (
                <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums">
                  {row.hours === undefined ? "—" : formatHours(row.hours)}
                </td>
              ) : null}
              <td className="px-4 py-2.5 text-right">
                <MtdMargin value={row.margin_pct} priced={pricedSoFar(row)} />
              </td>
              {full ? (
                <>
                  <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums">
                    {row.landing_hours === undefined ? "—" : formatHours(row.landing_hours)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums">
                    {row.landing_revenue === undefined ? "—" : money(row.landing_revenue)}
                  </td>
                </>
              ) : null}
              <td className="px-4 py-2.5 text-right">
                <MtdMargin value={row.landing_margin_pct} priced={pricedLanding(row)} />
              </td>
              <td className="min-w-32 px-4 py-2.5">
                <MtdFlags row={row} />
              </td>
            </tr>
          ))}
        </DataTable>
      </Card>

      {idle.length > 0 ? (
        <p className="text-xs text-muted-foreground">
          Pending discounts on projects with no time this month, not in any figure above:{" "}
          {idle
            .map((item) => `${item.demand_name ?? item.demand_id} ${money(item.amount)}`)
            .join(", ")}
          .
        </p>
      ) : null}
    </div>
  );
}
